import { useCallback, useRef } from 'react';

// Sent chunks kept for replay after a reconnect, covering audio Corti never transcribed.
const SENT_TAIL_CHUNKS = 4;

export function useChunkQueue() {
  const chunks = useRef<Blob[]>([]);
  const sentTail = useRef<Blob[]>([]);
  const ready = useRef(false);
  const draining = useRef(false);
  const sendChunkRef = useRef<(chunk: Blob) => Promise<void>>(() => Promise.resolve());
  const onErrorRef = useRef<(err: unknown) => void>(() => {});
  const isCancelledRef = useRef<() => boolean>(() => false);

  const drain = useCallback(async () => {
    if (draining.current || !ready.current) return;
    draining.current = true;
    while (chunks.current.length > 0 && !isCancelledRef.current()) {
      const chunk = chunks.current[0];
      try {
        await sendChunkRef.current(chunk);
        chunks.current.shift();
        sentTail.current.push(chunk);
        if (sentTail.current.length > SENT_TAIL_CHUNKS) sentTail.current.shift();
      } catch (err) {
        onErrorRef.current(err);
        draining.current = false;
        return;
      }
    }
    draining.current = false;
  }, []);

  // Call at the start of each recording session to reset state and bind callbacks.
  const init = useCallback(
    (
      sendChunk: (chunk: Blob) => Promise<void>,
      onError: (err: unknown) => void,
      isCancelled: () => boolean
    ) => {
      chunks.current = [];
      sentTail.current = [];
      ready.current = false;
      draining.current = false;
      sendChunkRef.current = sendChunk;
      onErrorRef.current = onError;
      isCancelledRef.current = isCancelled;
    },
    []
  );

  const enqueue = useCallback(
    (chunk: Blob) => {
      chunks.current.push(chunk);
      void drain();
    },
    [drain]
  );

  const setReady = useCallback(async () => {
    ready.current = true;
    await drain();
  }, [drain]);

  const pause = useCallback(() => {
    ready.current = false;
  }, []);

  const requeueSentTail = useCallback((headerChunk: Blob | null) => {
    const replay = sentTail.current;
    sentTail.current = [];
    if (headerChunk && !replay.includes(headerChunk) && chunks.current[0] !== headerChunk) {
      replay.unshift(headerChunk);
    }
    chunks.current = [...replay, ...chunks.current];
  }, []);

  return { init, enqueue, setReady, pause, requeueSentTail };
}
